import styled from '@emotion/styled';
import { colors, Text } from '@innovationdepartment/proxima-ui';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router';

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TagChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: ${colors.neutral10};
  border-radius: 16px;
  font-size: 14px;
  margin-top: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral20};
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.neutral60};
  transition: color 0.2s;

  &:hover {
    color: ${colors.red50};
  }

  svg {
    font-size: 16px;
  }
`;

const EmptyState = styled.div`
  color: ${colors.neutral60};
  font-style: italic;
`;

const LoadingState = styled.div`
  color: ${colors.neutral60};
`;

type BrandTag = {
  id: string;
  originalName: string;
  displayName: string;
  category: string;
  subCategory: string;
};

type BrandTagsViewProps = {
  brandTags: BrandTag[];
  loading: boolean;
  onRemoveTag: (tagId: string) => void;
};

const BrandTagsView = ({ brandTags, loading, onRemoveTag }: BrandTagsViewProps) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <LoadingState>
        <Text variant="body2">Loading tags...</Text>
      </LoadingState>
    );
  }

  if (brandTags.length === 0) {
    return (
      <EmptyState>
        <Text variant="body2">No tags assigned to this brand</Text>
      </EmptyState>
    );
  }

  const handleTagClick = (tagId: string) => {
    navigate(`/brand-tags/${tagId}`);
  };

  const handleRemoveClick = (e: React.MouseEvent, tagId: string) => {
    e.stopPropagation();
    onRemoveTag(tagId);
  };

  return (
    <TagsWrapper>
      {brandTags.map(tag => (
        <TagChip key={tag.id} onClick={() => handleTagClick(tag.id)}>
          <Text variant="body2" color="neutral80">
            {tag.displayName}
          </Text>
          <RemoveButton onClick={e => handleRemoveClick(e, tag.id)} title="Remove tag">
            <CloseIcon />
          </RemoveButton>
        </TagChip>
      ))}
    </TagsWrapper>
  );
};

export default BrandTagsView;
